import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CompanyService, Company } from '../../../services/company.service';
import { Subject, takeUntil } from 'rxjs';
import { LoggerService } from '../../../services/logger.service';
import { formatCurrencyWithSymbol } from '../../../utils/currency.utils';
import { COUNTRIES, getCountry, supportsNetworks } from '../../../config/countries.config';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideChevronRight, lucideInfo, lucideUsers } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmItemImports } from '@spartan-ng/helm/item';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { HlmSwitchImports } from '@spartan-ng/helm/switch';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';

@Component({
  selector: 'app-verbund-settings',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmItemImports,
    HlmSpinnerImports,
    HlmSwitchImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideCheck,
      lucideChevronRight,
      lucideInfo,
      lucideUsers,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './verbund-settings.component.html',
})
export class VerbundSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  company: Company | null = null;

  /** Networks settle by SEPA transfer in euro: not every country may run one */
  get networksAvailable(): boolean {
    return supportsNetworks(this.company?.billing_country);
  }

  /** "Deutschland, Österreich und Italien" */
  readonly networkCountries = COUNTRIES.filter((c) => c.supportsNetworks)
    .map((c) => c.name)
    .join(', ')
    .replace(/, ([^,]*)$/, ' und $1');

  get countryPhrase(): string {
    return getCountry(this.company?.billing_country)?.inCountry ?? '';
  }

  /** The service fee per order in the company's currency, e.g. "0,99 €" */
  serviceFee(): string {
    return formatCurrencyWithSymbol(this.company?.customer_fee_cents ?? 99);
  }

  activating = false;
  activateSuccess = false;
  activateError: string | null = null;

  savingReporting = false;

  constructor(
    private companyService: CompanyService,
    private logger: LoggerService,
  ) {}

  ngOnInit() {
    this.companyService.company$.pipe(takeUntil(this.destroy$)).subscribe((company) => {
      this.company = company;
    });
  }

  toggleGroupEnabled(enable: boolean) {
    if (!this.company) return;
    if (!enable && this.company.is_member) return;
    this.activating = true;
    this.activateError = null;
    this.activateSuccess = false;
    this.companyService
      .updateCompanyDetails({
        public_id: this.company.public_id,
        is_group_admin: enable,
      })
      .subscribe({
        next: () => {
          this.activating = false;
          this.activateSuccess = true;
          setTimeout(() => (this.activateSuccess = false), 3000);
        },
        error: (err) => {
          this.activating = false;
          this.activateError = err.error?.detail || 'Fehler beim Speichern.';
        },
      });
  }

  /** Explainer content — data instead of repeated markup. */
  readonly centralDuties = [
    'Erstellt und verkauft die Gutscheine',
    'Verkaufserlöse gehen wie gewohnt auf sein Bankkonto',
    'Lädt Mitglieder per Link ein & gibt sie frei',
    'Überweist Mitgliedern monatlich ihre Einlösungen',
    'Löst selbst keine Gutscheine ein – das machen die Mitglieder',
  ];

  readonly memberDuties = [
    'Registriert sich über den Einladungslink',
    'Löst Gutscheine per Dashboard oder PIN ein',
    'Hinterlegt seine IBAN, braucht kein Stripe-Konto',
    'Erhält monatlich eine Überweisung der Zentrale',
  ];

  readonly moneyFlow = [
    { title: '1. Kauf', detail: 'Kunde kauft Gutschein' },
    { title: '2. Auszahlung', detail: 'Erlös → Bankkonto der Zentrale' },
    { title: '3. Einlösung', detail: 'Mitglied löst ein' },
    { title: '4. Monatsabrechnung', detail: 'Zentrale überweist per Sammelüberweisung' },
  ];

  readonly redemptionMethods = [
    { title: 'Per Dashboard', detail: 'Mitglied loggt sich ein und löst unter „Einlösen“ ein.' },
    {
      title: 'Per PIN',
      detail:
        'Mitarbeiter wählt sein Unternehmen auf der öffentlichen Seite und gibt seinen PIN ein.',
    },
    {
      title: 'Voraussetzungen',
      detail: 'Mitglied muss von der Zentrale freigegeben sein.',
    },
  ];

  toggleMembersCanSeeReporting(value: boolean) {
    if (!this.company) return;
    this.savingReporting = true;
    this.companyService
      .updateCompanyDetails({
        public_id: this.company.public_id,
        members_can_see_reporting: value,
      })
      .subscribe({
        next: () => {
          this.savingReporting = false;
        },
        error: (err) => {
          this.savingReporting = false;
          this.logger.error('Error saving reporting setting:', err);
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
